import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import WizardLayout from "../components/WizardLayout";
import RFQAnalysisLoader from "../components/rfq/RFQAnalysisLoader";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowLeft, FileText, Image as ImageIcon, Loader2, Plus, Sparkles, X } from "lucide-react";
import {
  analyzeRfqIntelligence,
  uploadSpec,
  buildConfirmationState,
  saveBuyerSession,
  getBuyerSession,
  getWorkspaceMe,
} from "../lib/api";
import { WIZARD } from "../constants/testIds";

const EXAMPLE =
  "I need tomato paste 28–30 Brix for Ghana, one container, private label.";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function isValidPhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

function newLocalId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function RFQWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromWorkspace = searchParams.get("from") === "workspace";
  const fileInputRef = useRef(null);
  const productEndRef = useRef(null);
  const [acceptTypes, setAcceptTypes] = useState(".pdf,.docx,.jpg,.jpeg,.png");
  const [products, setProducts] = useState([{ id: newLocalId(), text: "" }]);
  const [attachments, setAttachments] = useState([]);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerCompany, setBuyerCompany] = useState("");
  const [workspaceSession, setWorkspaceSession] = useState(null);
  const [profileLoading, setProfileLoading] = useState(fromWorkspace);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (!fromWorkspace) {
      setProfileLoading(false);
      return;
    }
    const session = getBuyerSession();
    if (!session.token) {
      navigate("/workspace/login", { replace: true });
      return;
    }
    setWorkspaceSession(session);
    (async () => {
      try {
        const profile = await getWorkspaceMe();
        const buyer = profile.buyer || {};
        setBuyerName(buyer.name || "");
        setBuyerEmail(buyer.email || "");
        setBuyerPhone(buyer.phone || "");
        setBuyerCompany(buyer.company || "");
        setWorkspaceSession({
          buyer_id: profile.buyer_id,
          workspace_id: profile.workspace_id,
          token: session.token,
        });
      } catch {
        navigate("/workspace/login", { replace: true });
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [fromWorkspace, navigate]);

  const multi = products.length > 1;
  const primaryText = products[0]?.text || "";
  const hasWorkspaceProfile = Boolean(workspaceSession?.buyer_id && workspaceSession?.workspace_id);
  const emailValid = hasWorkspaceProfile || isValidEmail(buyerEmail);
  const phoneValid = !buyerPhone.trim() || isValidPhone(buyerPhone);
  const canAnalyze =
    primaryText.trim() && emailValid && phoneValid && !uploading && !profileLoading;

  const handleUploadClick = (mode) => {
    if (mode === "pdf") setAcceptTypes(".pdf,.docx");
    else setAcceptTypes(".jpg,.jpeg,.png");
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const attachment = await uploadSpec(file);
      setAttachments((prev) => [...prev, attachment]);
      toast.success(`Uploaded ${attachment.filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (fileId) => {
    setAttachments((prev) => prev.filter((a) => a.file_id !== fileId));
  };

  const setProductText = (id, text) => {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, text } : p)));
  };

  const addProduct = () => {
    setProducts((prev) => [...prev, { id: newLocalId(), text: "" }]);
    setTimeout(() => {
      productEndRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const removeProduct = (id) => {
    if (products.length <= 1) return;
    if (!window.confirm("Remove this product?")) return;
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const handleAnalyze = async () => {
    const filled = products.map((p) => ({ ...p, text: (p.text || "").trim() })).filter((p) => p.text);
    if (!filled.length) {
      toast.error("Please describe the product you need.");
      return;
    }
    if (!emailValid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!phoneValid) {
      toast.error("Please enter a valid phone number with country code.");
      return;
    }
    if (analyzing) return;
    setAnalyzing(true);
    try {
      const res = await analyzeRfqIntelligence({
        original_text: filled[0].text,
        products: filled.map((p) => ({ original_text: p.text })),
        attachment_ids: attachments.map((a) => a.file_id),
        buyer_name: buyerName.trim() || undefined,
        buyer_email: buyerEmail.trim() || undefined,
        buyer_phone: buyerPhone.trim() || undefined,
        buyer_company: buyerCompany.trim() || undefined,
        buyer_id: workspaceSession?.buyer_id,
        workspace_id: workspaceSession?.workspace_id,
        channel: hasWorkspaceProfile ? "workspace" : "web",
      });
      if (res.buyer_complete) {
        if (res.session_token) saveBuyerSession(res);
        toast.success("Your product request is ready.");
        navigate(`/rfq/${res.rfq_id}/confirmation`, { state: buildConfirmationState(res) });
      } else {
        navigate(`/rfq/${res.rfq_id}/questions`, { state: { intelligence: res } });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed. Please try again.");
      setAnalyzing(false);
    }
  };

  if (analyzing || profileLoading) {
    return (
      <WizardLayout
        stepIndex={0}
        totalSteps={3}
        eyebrow="Analyzing"
        title="Reading your request"
        subtitle="We extract product, quantity, destination and specs automatically."
      >
        <RFQAnalysisLoader />
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      stepIndex={0}
      totalSteps={3}
      eyebrow="Product request"
      title="What product are you looking for?"
      subtitle="Describe the product, quantity, destination and any specifications you already know."
      footer={
        <div className="flex items-center justify-between" data-testid={WIZARD.container}>
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            data-testid={WIZARD.backBtn}
            className="rounded-md h-11 px-4 text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </Button>
          <Button
            onClick={handleAnalyze}
            data-testid={WIZARD.analyzeBtn}
            disabled={!canAnalyze}
            className="btn-wizard-primary"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Analyze My Request
          </Button>
        </div>
      }
    >
      <div className="card-panel p-6 sm:p-8 space-y-8 shadow-glow transition-shadow duration-300 hover:shadow-lift border-t-4 border-t-[hsl(var(--brand-red))]">
        <div>
          <div className="section-label mb-4">Your request</div>
          <div className="space-y-5">
            {products.map((p, index) => (
              <div
                key={p.id}
                className={multi ? "card-panel p-4 border-l-4 border-l-[hsl(var(--brand-red))]" : ""}
                data-testid={WIZARD.productBlock}
              >
                {multi && (
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[hsl(var(--brand-navy))] text-white text-xs font-bold grid place-items-center">
                        {index + 1}
                      </span>
                      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                        Product {index + 1}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeProduct(p.id)}
                      data-testid={WIZARD.removeProductBtn}
                      className="text-xs uppercase tracking-[0.12em] font-semibold text-neutral-400 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <Textarea
                  data-testid={index === 0 ? WIZARD.requestText : `${WIZARD.requestText}-${index + 1}`}
                  value={p.text}
                  onChange={(e) => setProductText(p.id, e.target.value)}
                  placeholder={EXAMPLE}
                  className="field-input min-h-[180px] text-base leading-relaxed resize-y"
                />
              </div>
            ))}
          </div>
          <div ref={productEndRef} />

          {products.length === 1 && (
            <button
              type="button"
              onClick={() => setProductText(products[0].id, EXAMPLE)}
              className="mt-4 text-xs uppercase tracking-[0.18em] font-semibold text-[hsl(217,90%,25%)] hover:text-[hsl(217,90%,18%)] transition-colors duration-200"
            >
              Use example
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={addProduct}
          data-testid={WIZARD.addProductBtn}
          className="add-dashed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Product
        </button>

        <div>
          <div className="section-label mb-4">Optional attachment</div>
          <input
            type="file"
            ref={fileInputRef}
            data-testid={WIZARD.fileInput}
            onChange={handleFileChange}
            accept={acceptTypes}
            className="hidden"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid={WIZARD.uploadPdfBtn}
              disabled={uploading}
              onClick={() => handleUploadClick("pdf")}
              className="upload-tile group"
            >
              <div className="icon-tile">
                <FileText className="w-5 h-5 text-[hsl(217,90%,25%)]" />
              </div>
              <div className="mt-3 font-medium text-neutral-900">Upload PDF / DOCX</div>
              <div className="text-xs text-neutral-500 mt-1">Max 15 MB</div>
            </button>
            <button
              type="button"
              data-testid={WIZARD.uploadImageBtn}
              disabled={uploading}
              onClick={() => handleUploadClick("image")}
              className="upload-tile group"
            >
              <div className="icon-tile">
                <ImageIcon className="w-5 h-5 text-[hsl(217,90%,25%)]" />
              </div>
              <div className="mt-3 font-medium text-neutral-900">Upload Image</div>
              <div className="text-xs text-neutral-500 mt-1">JPG or PNG</div>
            </button>
          </div>
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-neutral-500 mt-4 px-1">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </div>
          )}
          {attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              {attachments.map((a) => (
                <div
                  key={a.file_id}
                  className="flex items-center justify-between p-3 rounded-lg border border-neutral-200 bg-neutral-50/80"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-[hsl(217,90%,25%)] shrink-0" />
                    <span className="text-sm truncate font-medium">{a.filename}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.file_id)}
                    data-testid={WIZARD.removeFileBtn}
                    className="p-1 rounded text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {hasWorkspaceProfile ? (
            <div className="rounded-lg border border-[hsl(var(--brand-navy))]/15 bg-[hsl(var(--brand-navy))]/[0.03] p-5">
              <div className="section-label mb-2">Your workspace profile</div>
              <p className="text-sm text-neutral-600">
                {buyerCompany || buyerName || "Buyer"}
                {buyerEmail ? ` · ${buyerEmail}` : ""}
                {buyerPhone ? ` · ${buyerPhone}` : ""}
              </p>
              <p className="mt-2 text-xs text-neutral-500">
                Contact details are taken from your workspace — just describe the product below.
              </p>
            </div>
          ) : (
            <>
              <div className="section-label mb-4">Your contact</div>
              <p className="text-sm text-neutral-500 mb-4">
                We&apos;ll send quotations and updates to this address. Add WhatsApp for faster
                updates.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="buyer-name" className="text-neutral-700">
                    Your name <span className="text-neutral-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="buyer-name"
                    data-testid={WIZARD.buyerNameInput}
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    placeholder="John Smith"
                    className="field-input h-11"
                    autoComplete="name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buyer-company" className="text-neutral-700">
                    Company <span className="text-neutral-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="buyer-company"
                    data-testid={WIZARD.buyerCompanyInput}
                    value={buyerCompany}
                    onChange={(e) => setBuyerCompany(e.target.value)}
                    placeholder="Acme Trading Ltd"
                    className="field-input h-11"
                    autoComplete="organization"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="buyer-email" className="text-neutral-700">
                    Email address <span className="text-[hsl(var(--brand-red))]">*</span>
                  </Label>
                  <Input
                    id="buyer-email"
                    type="email"
                    data-testid={WIZARD.buyerEmailInput}
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="field-input h-11"
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="buyer-phone" className="text-neutral-700">
                    WhatsApp / phone{" "}
                    <span className="text-neutral-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="buyer-phone"
                    type="tel"
                    data-testid={WIZARD.buyerPhoneInput}
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                    placeholder="+233 24 123 4567"
                    className="field-input h-11"
                    autoComplete="tel"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </WizardLayout>
  );
}
